// Activity trail — renders the agent's tool calls, subagent transitions,
// and artifact pickers in chronological order. Mirrors the legacy demo's
// rendering at minimum; richer formatting (collapsible tool results) can
// be layered in later.

import {
  ChevronDown,
  ChevronRight,
  Sparkles,
  Wrench,
} from "lucide-react";
import { useState } from "react";

import { AskUserQuestionCard } from "./AskUserQuestionCard";
import { TodoListCard } from "./TodoListCard";
import { cn } from "@/lib/utils";
import type { ActivityEntry, TodoItem } from "@/lib/types";

export function ActivityTrail({
  activity,
  todos,
}: {
  activity: ActivityEntry[];
  todos?: TodoItem[];
}) {
  const filtered = activity.filter((e) => e.kind !== "error");
  if (filtered.length === 0 && (!todos || todos.length === 0)) return null;

  return (
    <div className="space-y-3">
      {todos && todos.length > 0 && <TodoListCard todos={todos} />}
      {filtered.map((entry, i) => (
        <ActivityEntryRow key={i} entry={entry} />
      ))}
    </div>
  );
}

function ActivityEntryRow({ entry }: { entry: ActivityEntry }) {
  if (entry.kind === "tool") {
    return <ToolEntry entry={entry} />;
  }
  if (entry.kind === "subagent_start") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground pl-1">
        <Sparkles className="h-3.5 w-3.5" />
        <span className="font-mono uppercase tracking-wider">
          subagent · {entry.agent_type ?? "?"}
        </span>
      </div>
    );
  }
  if (entry.kind === "subagent_stop") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground/60 pl-1">
        <span className="font-mono uppercase tracking-wider">
          ↳ subagent done · {entry.agent_type ?? "?"}
        </span>
      </div>
    );
  }
  if (entry.kind === "artifact" && entry.kind_subtype === "ask_user_question") {
    // Cast through unknown — the SSE consumer guarantees this shape.
    return (
      <AskUserQuestionCard
        data={entry.data as { qid: string; questions: { question: string; options?: string[]; multi?: boolean }[]; thread_id: string }}
      />
    );
  }
  if (entry.kind === "artifact") {
    return (
      <div className="rounded-md border border-border bg-card/50 p-3 text-xs text-muted-foreground">
        <span className="font-mono uppercase tracking-wider">
          artifact · {entry.kind_subtype}
        </span>
      </div>
    );
  }
  return null;
}

function ToolEntry({
  entry,
}: {
  entry: Extract<ActivityEntry, { kind: "tool" }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasResult = entry.result !== null && entry.result !== "";

  return (
    <div className="rounded-md border border-border bg-card/50">
      <button
        onClick={() => hasResult && setExpanded((v) => !v)}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 text-left",
          hasResult && "hover:bg-accent/50 cursor-pointer"
        )}
      >
        <Wrench className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground shrink-0">
          {entry.name || entry.slug}
        </span>
        {entry.description && (
          <span className="text-xs text-muted-foreground/70 truncate flex-1 text-left">
            · {entry.description}
          </span>
        )}
        {hasResult && (
          <span className="text-muted-foreground/50 shrink-0">
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </span>
        )}
      </button>
      {expanded && hasResult && (
        <pre className="px-3 py-2 border-t border-border text-[11px] leading-relaxed text-muted-foreground font-mono overflow-x-auto whitespace-pre-wrap">
          {/* Server emits tool results as strings AND occasionally as
              JSON objects. Coerce defensively so we never throw the
              "Objects are not valid as a React child" error. */}
          {typeof entry.result === "string"
            ? entry.result
            : JSON.stringify(entry.result, null, 2)}
        </pre>
      )}
    </div>
  );
}
